import { fromJS, List as ImmutableList } from 'immutable';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isAttributionReport } from 'reporting-data/tsTypes/reportTypes';
import { ATTRIBUTION_TOUCH_POINTS, CONTACT_CREATE_ATTRIBUTION, CRM_OBJECT, DEAL_CREATE_ATTRIBUTION } from 'reporting-data/constants/dataTypes/inboundDb';
import { HUBSPOT_OBJECT_COORDINATES_TO_DATA_TYPE } from 'reporting-data/constants/objectCoordinates';
import { DATA_WELL } from 'reporting-data/constants/chartTypes';
import { jsx as _jsx } from "react/jsx-runtime";
const DEAL_AND_REVENUE_CONFIG_METRICS = ImmutableList([{
  // This is the Associated Deal Amount value
  property: 'hs_deal_amount',
  /*
  The MIN measure is the input to the transform aggregations - essentially the
  "associated amount by deal" since the config has deal id as a dimensiton.
  SUM and AVG are here so that the final resulting measures have labels
  available - which is hacky and may not be in the final version of this.
   */
  metricTypes: ['MIN', 'SUM', 'AVG'],
  percentiles: []
}, {
  /*
  Since hs_deal_id is also the dimension on the contact, the DISTINCT_APPROX
  metric will be "1" on each row, but that can be summed via transform to give
  us a total count of deals.
  */
  property: 'hs_deal_id',
  metricTypes: ['DISTINCT_APPROX'],
  percentiles: []
}, {
  /*
  The special "count" property gives measures the count of primary objects
  being reported on, so for attribution: count of touchpoints. Since the
  config's dimension is deal, this gives us the number of touchpoints
  associated with each deal in the initial dataset, and we can transform with
  an average to get average number of touchpoints per deal.
  */
  property: 'count',
  metricTypes: ['SUM'],
  percentiles: []
}]);
const CONTACT_CREATE_ATTRIBUTION_CONFIG_METRICS = ImmutableList([{
  property: 'hs_contact_id',
  metricTypes: ['DISTINCT_APPROX'],
  percentiles: []
}, {
  property: 'count',
  metricTypes: ['SUM'],
  percentiles: []
}]);
const CONFIG_METRICS_BY_ATTRIBUTION_TYPE = {
  [ATTRIBUTION_TOUCH_POINTS]: DEAL_AND_REVENUE_CONFIG_METRICS,
  [DEAL_CREATE_ATTRIBUTION]: DEAL_AND_REVENUE_CONFIG_METRICS,
  [CONTACT_CREATE_ATTRIBUTION]: CONTACT_CREATE_ATTRIBUTION_CONFIG_METRICS
};
const CONTACT_CREATE_ATTRIBUTION_AGGREGATIONS = [{
  as: 'DISTINCT_APPROX|hs_contact_id',
  op: 'sum',
  field: 'DISTINCT_APPROX|hs_contact_id'
}, {
  as: 'SUM|count',
  op: 'average',
  field: 'SUM|count'
}];
const DEAL_AND_REVENUE_ATTRIBUTION_AGGREGATIONS = [{
  // Sum of all minimum deal amounts per deal = total associated deal amount
  as: 'SUM|hs_deal_amount',
  op: 'sum',
  field: 'MIN|hs_deal_amount'
}, {
  // Sum of count of deals per deal (always 1) = total number of deals
  as: 'DISTINCT_APPROX|hs_deal_id',
  op: 'sum',
  field: 'DISTINCT_APPROX|hs_deal_id'
}, {
  // Average of minimum deal amount per deal = average deal amount per deal
  as: 'AVG|hs_deal_amount',
  op: 'average',
  field: 'MIN|hs_deal_amount'
}, {
  // Average of count of touchpoints by deal = average touchpoints per deal
  as: 'SUM|count',
  op: 'average',
  field: 'SUM|count'
}];
const AGGREGATIONS_BY_DATA_TYPE = {
  [ATTRIBUTION_TOUCH_POINTS]: DEAL_AND_REVENUE_ATTRIBUTION_AGGREGATIONS,
  [DEAL_CREATE_ATTRIBUTION]: DEAL_AND_REVENUE_ATTRIBUTION_AGGREGATIONS,
  [CONTACT_CREATE_ATTRIBUTION]: CONTACT_CREATE_ATTRIBUTION_AGGREGATIONS
};
const DEAL_AND_REVENUE_ATTRIBUTION_VIZ_FIELDS = [{
  field: 'SUM|hs_deal_amount',
  dataset: 'primary'
}, {
  field: 'DISTINCT_APPROX|hs_deal_id',
  dataset: 'primary'
}, {
  field: 'AVG|hs_deal_amount',
  dataset: 'primary'
}, {
  field: 'SUM|count',
  dataset: 'primary'
}];

// This instructs the visualization to display the output of the transforms
const VIZ_FIELDS = {
  [ATTRIBUTION_TOUCH_POINTS]: DEAL_AND_REVENUE_ATTRIBUTION_VIZ_FIELDS,
  [DEAL_CREATE_ATTRIBUTION]: DEAL_AND_REVENUE_ATTRIBUTION_VIZ_FIELDS,
  [CONTACT_CREATE_ATTRIBUTION]: [{
    field: 'DISTINCT_APPROX|hs_contact_id',
    dataset: 'primary'
  }, {
    field: 'SUM|count',
    dataset: 'primary'
  }]
};
const FIELD_TO_LABEL_KEY = dataType => ({
  'SUM|hs_deal_amount': 'associatedDealAmount',
  'DISTINCT_APPROX|hs_deal_id': dataType === DEAL_CREATE_ATTRIBUTION ? 'dealsCreated' : 'dealCount',
  'AVG|hs_deal_amount': 'averageDealAmount',
  'SUM|count': dataType === CONTACT_CREATE_ATTRIBUTION ? 'touchpointsPerContact' : 'touchpointsPerDeal',
  'DISTINCT_APPROX|hs_contact_id': 'contactCount'
});
function getFullLangKey(labelKey) {
  return `reporting-enablement.singleReportViewer.summaryDatawellInsight.kpis.contextualKpis.attribution.${labelKey}`;
}
const getAttributionDataType = baseReport => {
  const dataType = baseReport.getIn(['config', 'dataType']);
  if (dataType === CRM_OBJECT || !dataType) {
    return HUBSPOT_OBJECT_COORDINATES_TO_DATA_TYPE.get(baseReport.getIn(['config', 'objectTypeId']));
  }
  return dataType;
};
function renderCustomHeaderLabel(cell, dataType) {
  const labelKey = FIELD_TO_LABEL_KEY(dataType)[cell.field];
  return labelKey ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: getFullLangKey(labelKey)
  }) : cell.label;
}
function transformReport({
  baseReport,
  kpiLimit
}) {
  const dataType = getAttributionDataType(baseReport);
  const limitedVizFields = kpiLimit ? VIZ_FIELDS[dataType].slice(0, kpiLimit) : VIZ_FIELDS[dataType];
  const configMetrics = CONFIG_METRICS_BY_ATTRIBUTION_TYPE[dataType];
  return baseReport.withMutations(mutableReport => mutableReport.set('chartType', DATA_WELL).update('config', config => config.set('dimensions', ImmutableList([dataType === CONTACT_CREATE_ATTRIBUTION ? 'hs_contact_id' : 'hs_deal_id'])).set('metrics', configMetrics).set('limit', 5000)).set('visualization', fromJS({
    type: 'DATAWELL',
    layer: {
      fields: [],
      dataset: 'primary',
      direction: 'horizontal',
      transform: [{
        type: 'AGGREGATE',
        aggregations: AGGREGATIONS_BY_DATA_TYPE[dataType]
      }],
      fieldOptions: limitedVizFields,
      valueFormattingOptions: []
    }
  })).setIn(['displayParams', 'render', 'header'], cell => renderCustomHeaderLabel(cell, dataType)));
}
function canTransformRevenueAttributionReport({
  baseReport
}) {
  return isAttributionReport(baseReport);
}
const AttributionKPIs = {
  isSupported: canTransformRevenueAttributionReport,
  transform: transformReport
};
export default AttributionKPIs;